import {
  type AuthenticationResultType,
  CognitoIdentityProviderClient,
  ConfirmForgotPasswordCommand,
  ForgotPasswordCommand,
  GlobalSignOutCommand,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import {
  createSrpSession,
  signSrpSession,
  wrapAuthChallenge,
  wrapInitiateAuth,
} from "cognito-srp-helper";
import { jwtDecode } from "jwt-decode";
import { User } from "oidc-client-ts";

import type {
  ActionResult,
  ErrorResult,
  PasswordSignInResult,
} from "shell/platform/types/types";

import {
  getUserManager,
  storeAuthProvider,
  storeCognitoAuthMode,
} from "shell/platform/lib/oidcClient";

const SCOPE = "openid profile email";

const getRegion = () => {
  const region = import.meta.env.VITE_COGNITO_REGION;
  if (!region) throw new Error("No VITE_COGNITO_REGION provided");
  return region;
};

const getClientId = () => {
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  if (!clientId) throw new Error("No VITE_COGNITO_CLIENT_ID provided");
  return clientId;
};

const getUserPoolId = () => {
  const poolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  if (!poolId) throw new Error("No VITE_COGNITO_USER_POOL_ID provided");
  return poolId;
};

let cachedClient: CognitoIdentityProviderClient | null = null;
const getClient = () => {
  cachedClient ??= new CognitoIdentityProviderClient({ region: getRegion() });

  return cachedClient;
};

const toErrorResult = (error: unknown): ErrorResult => {
  const name =
    error && typeof error === "object" && "name" in error
      ? String(error.name)
      : "UnknownError";
  const message =
    error instanceof Error ? error.message : "Unknown authentication error";
  return { kind: "error", code: name, message };
};

const persistAuthResult = async (
  result: AuthenticationResultType,
): Promise<User> => {
  if (!result.IdToken || !result.AccessToken) {
    throw new Error("Cognito did not return id and access tokens");
  }

  const profile = jwtDecode<Record<string, unknown>>(result.IdToken);
  const expiresIn = result.ExpiresIn ?? 3600;
  const expiresAt = Math.floor(Date.now() / 1000) + expiresIn;

  const user = new User({
    id_token: result.IdToken,
    access_token: result.AccessToken,
    refresh_token: result.RefreshToken,
    token_type: result.TokenType ?? "Bearer",
    scope: SCOPE,
    profile: profile as ConstructorParameters<typeof User>[0]["profile"],
    expires_at: expiresAt,
    session_state: null,
  });

  storeAuthProvider("cognito");
  storeCognitoAuthMode("password");

  await getUserManager().storeUser(user);
  return user;
};

export const signInWithPassword = async (
  username: string,
  password: string,
): Promise<PasswordSignInResult> => {
  try {
    const clientId = getClientId();
    const client = getClient();

    const srpSession = createSrpSession(
      username,
      password,
      getUserPoolId(),
      false,
    );

    const initRes = await client.send(
      new InitiateAuthCommand(
        wrapInitiateAuth(srpSession, {
          AuthFlow: "USER_SRP_AUTH",
          ClientId: clientId,
          AuthParameters: { USERNAME: username },
        }),
      ),
    );

    const userIdForSrp =
      initRes.ChallengeParameters?.USER_ID_FOR_SRP ?? username;
    const sessionForSign = { ...srpSession, username: userIdForSrp };

    const signedSession = signSrpSession(sessionForSign, initRes);

    const respondRes = await client.send(
      new RespondToAuthChallengeCommand(
        wrapAuthChallenge(signedSession, {
          ChallengeName: "PASSWORD_VERIFIER",
          ClientId: clientId,
          Session: initRes.Session,
          ChallengeResponses: { USERNAME: userIdForSrp },
        }),
      ),
    );

    if (respondRes.AuthenticationResult) {
      await persistAuthResult(respondRes.AuthenticationResult);
      return { kind: "ok" };
    }

    const followUp =
      respondRes.ChallengeParameters?.USER_ID_FOR_SRP ?? username;

    if (respondRes.ChallengeName === "NEW_PASSWORD_REQUIRED") {
      return {
        kind: "newPasswordRequired",
        session: respondRes.Session ?? "",
        username: followUp,
      };
    }

    if (respondRes.ChallengeName) {
      return {
        kind: "mfaRequired",
        challenge: respondRes.ChallengeName,
        session: respondRes.Session ?? "",
        username: followUp,
      };
    }

    return {
      kind: "error",
      code: "UnexpectedResponse",
      message: "Unexpected response from Cognito",
    };
  } catch (error) {
    return toErrorResult(error);
  }
};

export const completeNewPasswordChallenge = async (
  username: string,
  newPassword: string,
  session: string,
): Promise<PasswordSignInResult> => {
  try {
    const response = await getClient().send(
      new RespondToAuthChallengeCommand({
        ChallengeName: "NEW_PASSWORD_REQUIRED",
        ClientId: getClientId(),
        Session: session,
        ChallengeResponses: {
          USERNAME: username,
          NEW_PASSWORD: newPassword,
        },
      }),
    );

    if (response.AuthenticationResult) {
      await persistAuthResult(response.AuthenticationResult);
      return { kind: "ok" };
    }

    if (response.ChallengeName) {
      return {
        kind: "mfaRequired",
        challenge: response.ChallengeName,
        session: response.Session ?? "",
        username,
      };
    }

    return {
      kind: "error",
      code: "UnexpectedResponse",
      message: "Unexpected response from Cognito",
    };
  } catch (error) {
    return toErrorResult(error);
  }
};

export const refreshCognitoTokens = async (
  refreshToken: string,
): Promise<User | null> => {
  try {
    const response = await getClient().send(
      new InitiateAuthCommand({
        AuthFlow: "REFRESH_TOKEN_AUTH",
        ClientId: getClientId(),
        AuthParameters: { REFRESH_TOKEN: refreshToken },
      }),
    );

    const result = response.AuthenticationResult;
    if (!result?.AccessToken || !result?.IdToken) return null;

    return await persistAuthResult({ ...result, RefreshToken: refreshToken });
  } catch {
    return null;
  }
};

const runReset = async (
  send: () => Promise<unknown>,
): Promise<ActionResult> => {
  try {
    await send();
    return { kind: "ok" };
  } catch (error) {
    return toErrorResult(error);
  }
};

export const requestPasswordReset = (username: string): Promise<ActionResult> =>
  runReset(() =>
    getClient().send(
      new ForgotPasswordCommand({
        ClientId: getClientId(),
        Username: username,
      }),
    ),
  );

export const confirmPasswordReset = (
  username: string,
  code: string,
  newPassword: string,
): Promise<ActionResult> =>
  runReset(() =>
    getClient().send(
      new ConfirmForgotPasswordCommand({
        ClientId: getClientId(),
        Username: username,
        ConfirmationCode: code,
        Password: newPassword,
      }),
    ),
  );

export const globalCognitoSignOut = async (
  accessToken: string,
): Promise<void> => {
  try {
    await getClient().send(
      new GlobalSignOutCommand({ AccessToken: accessToken }),
    );
  } catch {}
};
